import { forwardRef, KeyboardEvent, Ref } from 'react';
import type { ReactElement } from 'react';

import { onEnter } from '../../../utils';
import { FormLabel, PasswordInput } from '../../generic/Forms';
import Icon, { icons } from '../../generic/Icon';
import Tooltip from '../../generic/Tooltip';
import { AuthContextState, AuthInput } from '../context/AuthContext';

import PasswordValidationTooltip from './PasswordValidationTooltip';

type AuthFormPasswordInputProps = Pick<AuthContextState, 'step' | 'hidePasswordInput' | 'passwordStrength'> & {
  password: Omit<AuthInput, 'ref'>;
  onSubmit: () => void;
};

const AuthFormPasswordInput = ({
  step,
  password,
  passwordStrength,
  hidePasswordInput,
  onSubmit,
}: AuthFormPasswordInputProps, ref: Ref<HTMLInputElement>) => {
  if (step === 'enterEmail' || hidePasswordInput) {
    return null;
  }
  const requiredText: string | ReactElement = !passwordStrength
    ? '(Required)'
    : (
      <Tooltip
        tooltip={
          passwordStrength
            ? <PasswordValidationTooltip strength={passwordStrength} />
            : null
        }
        showTooltip={!!passwordStrength}
      >
        <span className="inline-block">
          <Icon icon={icons.infoCircle} />
        </span>
      </Tooltip>
    );

  const onKeyDown = onEnter((event: KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    onSubmit();
  });

  return (
    <FormLabel
      label="Password"
      requiredText={requiredText}
    >
      <PasswordInput
        ref={ref}
        label="Password"
        placeholder="Enter your password"
        onChange={password?.onChange}
        value={password?.value}
        onKeyDown={onKeyDown}
        size="large"
        testId="auth-password-input"
        isRequired
      />
    </FormLabel>
  );
};

export default forwardRef<HTMLInputElement, AuthFormPasswordInputProps>(AuthFormPasswordInput);
